import { Box, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { type JSX } from 'react';

import { url } from '../../appRouter/url.ts';
import {
  type Ablauf,
  type AkaDurchgangId,
  AkaThemaId,
  type AkaVeranstaltungId,
  ausschreibungStatus,
  AusschreibungStatus,
  type GetDurchgaengeByAkaProduktIdsOutput,
  type GetVeranstaltungenByAkaProduktIdsOutput,
  type Leistung,
  type SapStatus,
  type Terminart,
  type ThemaFromThemaRouter,
  type VivaStatus,
} from '../../dtos';
import { UpdateRessourcenBlockungen } from '../../pages/VeranstaltungDetailsPage/UpdateRessourcenBlockungen';
import {
  content,
  type DataGridColDef,
  determineStatusText,
  formatDate,
  getExperteNamesFromVeranstaltung,
  getLocationKuerzel,
  getVeranstaltungEnd,
  getVeranstaltungStart,
  printTerminart,
  printWithNoInfo,
  printWo,
} from '../../utils';
import { printMultiWo } from '../../utils/printWo.ts';
import { AusschreibungLink } from './AusschreibungLink.tsx';
import { ausschreibungStatusLabels, AusschreibungStatusTag } from './AusschreibungStatusTag.tsx';
import { getVeranstaltungenTrainers } from './DurchgaengeList';
import { LinkWithCopyButton } from './LinkWithCopyButton';
import { OtherStatusesCounter } from './OtherStatusesCounter.tsx';
import { LeistungIndexTag } from './ThemaHeader/LeistungIndexTag.tsx';
import { VaStatusChip } from './VaStatusChip';

export * from './TableColumnsRückmeldung.tsx';

export const akaVeranstaltungIdColumn = <
  T extends {
    akaVeranstaltungId: AkaVeranstaltungId;
    veranstaltungSapId?: number | null;
    akaDurchgangId?: AkaDurchgangId | null;
  },
>(): DataGridColDef<T>[number] => ({
  field: 'akaVeranstaltungId',
  flex: 1,
  headerName: content.columnLabels.akaVeranstaltungId,
  renderCell: ({ row: { akaVeranstaltungId, akaDurchgangId, veranstaltungSapId } }): JSX.Element => (
    <LinkWithCopyButton
      text={akaVeranstaltungId}
      to={
        akaDurchgangId
          ? url.durchgang.detailsForModultermin(akaDurchgangId, akaVeranstaltungId)
          : url.veranstaltung.details(akaVeranstaltungId, veranstaltungSapId)
      }
      tooltip={akaDurchgangId ? content.tooltips.zumModulterminWechseln : content.tooltips.zurVeranstaltungWechseln}
    />
  ),
});

export const leistungIdColumn = <T extends object>(
  getLeistung: (row: T) => Leistung | undefined,
): GridColDef<T, Leistung['leistungId'] | undefined> => ({
  field: 'leistungId',
  flex: 0.5,
  headerName: content.columnLabels.leistungId,
  valueGetter: (_value, row) => getLeistung(row)?.leistungId,
  renderCell: ({ row }) => {
    const leistung = getLeistung(row);
    return <AusschreibungLink leistung={leistung} />;
  },
});

export const leistungIndexColumn = <T extends object>(
  getLeistungstemplate: (row: T) => { index: number } | undefined,
): DataGridColDef<T>[number] => ({
  field: 'leistungIndex',
  flex: 0.5,
  headerName: content.columnLabels.leistungIndex,
  valueGetter: (_, row) => getLeistungstemplate(row)?.index,
  renderCell: ({ value }) => (typeof value === 'number' ? <LeistungIndexTag index={value} /> : content.noInfoAvailable),
});

export const statusAPColumn = <T extends object>(
  getStatus: (row: T) => AusschreibungStatus,
  getUniqueStatuses?: (row: T) => number,
): GridColDef<T, AusschreibungStatus> => ({
  field: 'statusAP',
  flex: 1,
  renderHeader: () => (
    <Tooltip title={content.tooltips.statusAP}>
      <span>{content.columnLabels.statusAP}</span>
    </Tooltip>
  ),
  valueGetter: (_, row) => getStatus(row),
  renderCell: ({ value, row }) => {
    const component =
      value && value !== ausschreibungStatus.KEINE_AUSSCHREIBUNG ? (
        <AusschreibungStatusTag status={value} shortenedLabel />
      ) : (
        <Typography title={ausschreibungStatusLabels[ausschreibungStatus.KEINE_AUSSCHREIBUNG]}>
          {content.noInfoAvailable}
        </Typography>
      );
    return <OtherStatusesCounter totalNumber={getUniqueStatuses?.(row) ?? 1}>{component}</OtherStatusesCounter>;
  },
});

export const akaDurchgangIdColumn = <T extends { akaDurchgangId: AkaDurchgangId }>(): DataGridColDef<T>[number] => ({
  field: 'akaDurchgangId',
  flex: 0.7,
  headerName: content.columnLabels.akaDurchgangId,
  renderCell: (params): JSX.Element => (
    <LinkWithCopyButton
      text={params.row.akaDurchgangId}
      to={url.durchgang.details(params.row.akaDurchgangId)}
      tooltip={content.tooltips.zumDurchgangWechseln}
    />
  ),
});

export const veranstaltungSapIdColumn = <
  T extends { veranstaltungSapId: number | null },
>(): DataGridColDef<T>[number] => ({
  field: 'veranstaltungSapId',
  flex: 0.5,
  headerName: content.columnLabels.veranstaltungSapId,
  valueGetter: (_value, row) => printWithNoInfo(row.veranstaltungSapId),
});

export const durchgangSapIdColumn = <T extends { durchgangSapId: number | null }>(): DataGridColDef<T>[number] => ({
  field: 'durchgangSapId',
  flex: 0.5,
  headerName: content.columnLabels.durchgangSapId,
  renderCell: (params): string => printWithNoInfo(params.row.durchgangSapId),
});

export const titelColumn = <
  T extends { titel: string } | { veranstaltungTitel: string },
>(): DataGridColDef<T>[number] => ({
  field: 'titel',
  flex: 1,
  headerName: content.columnLabels.titel,
  valueGetter: (_value, row) => printWithNoInfo('titel' in row ? row.titel : row.veranstaltungTitel),
});

export const ablaufStartColumn = <T extends { ablauf: Ablauf }>(): DataGridColDef<T>[number] => ({
  field: 'start',
  flex: 0.5,
  headerName: content.columnLabels.start,
  type: 'date',
  valueGetter: (_value, row) => getVeranstaltungStart(row),
  valueFormatter: (value: Date) => formatDate(value),
});

export const startColumn = <T extends { start: Date }>(): DataGridColDef<T>[number] => ({
  field: 'start',
  flex: 0.5,
  headerName: content.columnLabels.start,
  type: 'date',
  renderCell: (params) => formatDate(params.row.start),
});

export const ablaufEndColumn = <T extends { ablauf: Ablauf }>(): DataGridColDef<T>[number] => ({
  field: 'end',
  flex: 0.5,
  headerName: content.columnLabels.end,
  type: 'date',
  valueGetter: (_value, row): Date => getVeranstaltungEnd(row),
  valueFormatter: (value: Date) => formatDate(value),
});

export const endColumn = <T extends { end: Date }>(): DataGridColDef<T>[number] => ({
  field: 'end',
  flex: 0.5,
  headerName: content.columnLabels.end,
  type: 'date',
  renderCell: (params) => formatDate(params.row.end),
});

export const statusColumn = <T extends { vivaStatus: VivaStatus; sapStatus: SapStatus | null }>(
  getUniqueStatuses?: (row: T) => number,
): DataGridColDef<T>[number] => ({
  field: 'vivaStatus',
  flex: 0.5,
  headerName: content.columnLabels.status,
  valueGetter: (_value, row): string => determineStatusText(row.sapStatus, row.vivaStatus),
  renderCell: (params): JSX.Element => {
    const component = <VaStatusChip sapStatus={params.row.sapStatus} vivaStatus={params.row.vivaStatus} />;
    return <OtherStatusesCounter totalNumber={getUniqueStatuses?.(params.row) ?? 1}>{component}</OtherStatusesCounter>;
  },
});

export const kuerzelColumn = <
  T extends Parameters<typeof getLocationKuerzel>[number],
>(): DataGridColDef<T>[number] => ({
  field: 'kuerzel',
  flex: 0.5,
  headerName: content.columnLabels.kuerzel,
  valueGetter: (_value, row): string => printWithNoInfo(getLocationKuerzel(row)),
});

export const veranstaltungWoColumn = <T extends Parameters<typeof printWo>[number]>(): DataGridColDef<T>[number] => ({
  field: 'wo',
  flex: 0.5,
  headerName: content.columnLabels.wo,
  valueGetter: (_value, row): string => printWithNoInfo(printWo(row)),
});

export const durchgangWoColumn = <
  T extends { modultermine: GetDurchgaengeByAkaProduktIdsOutput[number]['modultermine'] },
>(): DataGridColDef<T>[number] => ({
  field: 'wo',
  flex: 0.7,
  headerName: content.columnLabels.wo,
  valueGetter: (_value, row): string => printWithNoInfo(printMultiWo(row.modultermine)),
});

export const expertsColumn = <
  T extends Parameters<typeof getExperteNamesFromVeranstaltung>[number],
>(): DataGridColDef<T>[number] => ({
  field: 'experts',
  flex: 1,
  headerName: content.columnLabels.experten,
  valueGetter: (_value, row): string => printWithNoInfo(getExperteNamesFromVeranstaltung(row)),
});

export const durchgangExpertsColumn = <
  T extends GetDurchgaengeByAkaProduktIdsOutput[number],
>(): DataGridColDef<T>[number] => ({
  field: 'durchgangExperts',
  flex: 1,
  headerName: content.columnLabels.experten,
  valueGetter: (_value, row) => printWithNoInfo(getVeranstaltungenTrainers(row.modultermine).join(', ')),
});

export const expertsEditColumn = <
  T extends GetVeranstaltungenByAkaProduktIdsOutput[number] & { thema: ThemaFromThemaRouter },
>(): DataGridColDef<T>[number] => ({
  field: 'experts',
  flex: 1,
  headerName: content.columnLabels.experten,
  valueGetter: (_value, row): string => printWithNoInfo(getExperteNamesFromVeranstaltung(row)),
  renderCell: (params): JSX.Element => {
    const value = typeof params.value === 'string' ? params.value : '';

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }} title={value}>
        <Typography variant="labelLight" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </Typography>
        <UpdateRessourcenBlockungen
          thema={params.row.thema}
          veranstaltung={params.row}
          editType="experte"
          isIconButton
        />
      </Box>
    );
  },
});

export const buchungsnummerColumn = <
  T extends { buchungsnummer: string; akaThemaId: AkaThemaId },
>(): DataGridColDef<T>[number] => ({
  field: 'buchungsnummer',
  flex: 0.5,
  headerName: content.columnLabels.buchungsnummer,
  renderCell: (params) => (
    <LinkWithCopyButton
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      text={params.value}
      to={url.thema.details(params.row.akaThemaId)}
      tooltip={content.tooltips.zumThemaWechseln}
    />
  ),
});

export const vNummerColumn = <T extends { vNummer: string | null }>(): DataGridColDef<T>[number] => ({
  field: 'vNummer',
  flex: 0.4,
  headerName: content.columnLabels.vNummer,
  valueGetter: (_value, row) => printWithNoInfo(row.vNummer),
});

export const modulnummerColumn = <
  T extends { modul: { modulnummer: number } | null },
>(): DataGridColDef<T>[number] => ({
  field: 'modulnummer',
  flex: 0.1,
  headerName: content.columnLabels.modulnummer,
  valueGetter: (_value, row) => row.modul?.modulnummer,
});

export const terminartColumn = <T extends { terminart: Terminart }>(): DataGridColDef<T>[number] => ({
  field: 'terminart',
  headerName: content.columnLabels.terminart,
  valueGetter: (_value, row) => printTerminart(row.terminart),
  flex: 0.5,
});

export const durchgangsartColumn = <T extends { durchgangsart: Terminart }>(): DataGridColDef<T>[number] => ({
  field: 'durchgangsart',
  headerName: content.columnLabels.durchgangsart,
  valueGetter: (_value, row) => printTerminart(row.durchgangsart),
  flex: 0.5,
});
